import { get, push, ref, remove, set, update } from "firebase/database";
import { fbDatabase } from "../firebase";
import { logBoardCreated } from "./analytics";
import { updateBoardMembership } from "./board";
import { createCategory, refCategories, refCategory } from "./category";
import type { Color } from "./category.types";
import { itemTransform, refItem, refItems } from "./item";
import { createSession } from "./session";

export interface TemplateColumn {
  title: string;
  description: string | null;
  color: Color;
}

export interface BoardTemplate {
  id: string;
  title: string;
  description: string;
  columns: TemplateColumn[];
}

export const TEMPLATES: BoardTemplate[] = [
  {
    id: "start-stop-continue",
    title: "Start / Stop / Continue",
    description: "Three columns mapping directly to behavior changes: what to start doing, what to stop doing, and what to continue doing. Best for new teams, new facilitators, or time-constrained sprints.",
    columns: [
      { title: "Start", description: "What should we start doing?", color: "green" },
      { title: "Stop", description: "What should we stop doing?", color: "red" },
      { title: "Continue", description: "What should we keep doing?", color: "blue" },
    ],
  },
  {
    id: "4ls",
    title: "4Ls",
    description: "Four quadrants capturing what participants Liked, Learned, Lacked, and Longed For. Best for milestone moments — end of a release, end of a project phase, or onboarding retrospectives.",
    columns: [
      { title: "Liked", description: "What did you enjoy?", color: "green" },
      { title: "Learned", description: "What did you learn?", color: "blue" },
      { title: "Lacked", description: "What was missing?", color: "red" },
      { title: "Longed For", description: "What do you wish for?", color: "yellow" },
    ],
  },
  {
    id: "sailboat",
    title: "Sailboat",
    description: "The team is a sailboat. Wind propels you, anchors hold you back, rocks are hidden risks, and the island is your destination. Best for teams stuck in repetitive patterns who need a fresh metaphor.",
    columns: [
      { title: "Wind", description: "What propelled us forward?", color: "green" },
      { title: "Anchor", description: "What held us back?", color: "red" },
      { title: "Rocks", description: "What hidden risks do we see?", color: "yellow" },
      { title: "Island", description: "Where do we want to go?", color: "blue" },
    ],
  },
  {
    id: "mad-sad-glad",
    title: "Mad / Sad / Glad",
    description: "Three emotional categories for processing how the team feels. Best for post-incident reviews, high-stress sprints, or after difficult organizational changes.",
    columns: [
      { title: "Mad", description: "What frustrated you?", color: "red" },
      { title: "Sad", description: "What disappointed you?", color: "blue" },
      { title: "Glad", description: "What made you happy?", color: "green" },
    ],
  },
  {
    id: "daki",
    title: "DAKI",
    description: "Drop, Add, Keep, Improve — more granular than Start/Stop/Continue. The Improve category captures things needing adjustment, not wholesale change. Best for mature teams refining established processes.",
    columns: [
      { title: "Drop", description: "What should we stop entirely?", color: "red" },
      { title: "Add", description: "What new things should we try?", color: "green" },
      { title: "Keep", description: "What's working well?", color: "blue" },
      { title: "Improve", description: "What needs tuning?", color: "yellow" },
    ],
  },
  {
    id: "starfish",
    title: "Starfish",
    description: "Five arms with gradient categories: Keep Doing, More Of, Less Of, Stop Doing, Start Doing. Distinguishes nuance that binary formats miss. Best for teams that have outgrown simpler formats.",
    columns: [
      { title: "Keep Doing", description: "What should we continue as-is?", color: "blue" },
      { title: "More Of", description: "What should we do more of?", color: "green" },
      { title: "Less Of", description: "What should we do less of?", color: "yellow" },
      { title: "Stop Doing", description: "What should we stop entirely?", color: "red" },
      { title: "Start Doing", description: "What new things should we begin?", color: "black" },
    ],
  },
  {
    id: "plus-delta",
    title: "Plus / Delta",
    description: "Two columns: what went well (plus) and what to change (delta). Uses 'delta' rather than 'minus' to frame improvements constructively. Best for short retros or meeting-end check-ins.",
    columns: [
      { title: "Plus", description: "What went well?", color: "green" },
      { title: "Delta", description: "What should change?", color: "blue" },
    ],
  },
  {
    id: "www",
    title: "What Went Well / Didn't / Improve",
    description: "The most universal default format. Three columns distilling retrospectives to their essence: celebrate, acknowledge, and commit. Best for a team's first retro or maximum simplicity.",
    columns: [
      { title: "Went Well", description: "What went well?", color: "green" },
      { title: "Didn't Go Well", description: "What didn't go well?", color: "red" },
      { title: "Can Improve", description: "What can we improve?", color: "blue" },
    ],
  },
  {
    id: "lean-coffee",
    title: "Lean Coffee",
    description: "No pre-set agenda. Participants propose topics, vote to prioritize, and discuss in priority order with strict timeboxes. Best for self-organizing teams or cross-team retros.",
    columns: [
      { title: "Topics", description: "Propose discussion topics", color: "blue" },
    ],
  },
  {
    id: "groceries",
    title: "Groceries",
    description: "Shared grocery and meal-planning template for coordinating shopping and meals. Best for households or small teams planning meals, splitting shopping tasks, and keeping a communal pantry list.",
    columns: [
      { title: "Veggies", description: "Vegetables and greens", color: "green" },
      { title: "Meat & Wine", description: "Proteins, cured meats, and wine", color: "red" },
      { title: "Aisles", description: "Pantry and packaged aisle items", color: "blue" },
      { title: "Dairy & Bread", description: "Dairy, eggs, and baked goods", color: "yellow" },
      { title: "Bulk Buy", description: "Large-quantity staples and deals", color: "black" },
      { title: "Hardware", description: "Household tools and supplies", color: "black" },
    ],
  },
  {
    id: "prevail",
    title: "Prevail!",
    description: "A practical action-tracking board that moves items from planning to completion, clarifying ownership and next steps. Best for teams or households that need a lightweight workflow to prioritize, assign, and close tasks.",
    columns: [
      { title: "To Do", description: "Backlog items and requests", color: "red" },
      { title: "Do Next", description: "High-priority items to tackle next", color: "yellow" },
      { title: "Delegate", description: "Tasks assigned to others", color: "green" },
      { title: "Follow Up", description: "Waiting on responses or blockers", color: "blue" },
      { title: "Don't Do", description: "Intentionally deprioritized items", color: "black" },
      { title: "Done", description: "Completed tasks", color: "black" },
    ],
  },
  {
    id: "gear-list",
    title: "Gear List",
    description: "A focused checklist for equipment and supplies: what's essential, what's nice-to-have, what you already own, and what to skip. Best for trip packing, shoot prep, or coordinated team purchases.",
    columns: [
      { title: "Need", description: "Essential items to acquire", color: "red" },
      { title: "Want", description: "Nice-to-have items", color: "yellow" },
      { title: "Have", description: "Items already owned", color: "green" },
      { title: "Skip", description: "Intentionally excluded items", color: "black" },
    ],
  },
  {
    id: "household-chores",
    title: "Household Chores",
    description: "A shared chore chart and kids' allowance board: a column per person, a bonus pool for extra tasks, and a running record of what got done each week. Best for families running weekly chores, allowances, and a fair split of who does what.",
    columns: [
      { title: "Bonus Pool", description: "Extra tasks anyone can claim to add to their allowance", color: "yellow" },
      { title: "Kid 1", description: "First child's weekly chores and allowance", color: "green" },
      { title: "Kid 2", description: "Second child's weekly chores and allowance", color: "blue" },
      { title: "Grown-ups", description: "What the adults are on the hook for", color: "red" },
    ],
  },
];

export async function createBoardFromTemplate(
  ownerUid: string,
  template: BoardTemplate,
): Promise<{ boardUid: string; sessionUid: string } | null> {
  const newBoardRef = await push(ref(fbDatabase, "boards/"));
  if (!newBoardRef.key) return null;

  const boardUid = newBoardRef.key;
  const epoch = Date.now();

  await set(newBoardRef, {
    memberUids: { [ownerUid]: epoch },
    ownerUid: ownerUid,
    createdDate: epoch,
    title: template.title,
    description: template.description,
    isPrivate: false,
  });

  await updateBoardMembership(ownerUid, boardUid);

  const sessionUid = await createSession(boardUid, false);
  if (!sessionUid) return null;

  await Promise.all(
    template.columns.map((col) =>
      createCategory(boardUid, sessionUid, col.title, col.description, col.color)
    )
  );

  logBoardCreated();
  return { boardUid, sessionUid };
}

export async function applyTemplateToSession(
  boardUid: string,
  sessionUid: string,
  template: BoardTemplate,
): Promise<void> {
  // Fetch existing items and categories
  const [itemsSnapshot, categoriesSnapshot] = await Promise.all([
    get(refItems(boardUid, sessionUid)),
    get(refCategories(boardUid, sessionUid)),
  ]);

  // Identify pinned vs non-pinned items
  const pinnedItemUids: string[] = [];
  const nonPinnedItemUids: string[] = [];
  if (itemsSnapshot.exists()) {
    itemsSnapshot.forEach((child) => {
      const data = child.val();
      if (data?.pinned) {
        pinnedItemUids.push(child.key!);
      } else {
        nonPinnedItemUids.push(child.key!);
      }
    });
  }

  // Collect old category UIDs
  const oldCategoryUids: string[] = [];
  if (categoriesSnapshot.exists()) {
    categoriesSnapshot.forEach((child) => {
      oldCategoryUids.push(child.key!);
    });
  }

  // Create new categories from template
  const newCategoryUids = await Promise.all(
    template.columns.map((col) =>
      createCategory(boardUid, sessionUid, col.title, col.description, col.color)
    )
  );

  const firstCategoryUid = newCategoryUids.find((uid) => uid != null);

  // Move pinned items to the first new category, delete non-pinned items
  await Promise.all([
    ...pinnedItemUids.map((itemUid) =>
      firstCategoryUid
        ? update(refItem(boardUid, sessionUid, itemUid), { categoryUid: firstCategoryUid })
        : remove(refItem(boardUid, sessionUid, itemUid))
    ),
    ...nonPinnedItemUids.map((itemUid) =>
      remove(refItem(boardUid, sessionUid, itemUid))
    ),
  ]);

  // Remove old categories
  await Promise.all(
    oldCategoryUids.map((categoryUid) =>
      remove(refCategory(boardUid, sessionUid, categoryUid))
    )
  );
}
